import { useEffect, useState, useRef } from 'react';
import { useDeepCompareCallback } from 'use-deep-compare';

import { validateInputs } from '.';

interface UseInputValidationInput {
    /**
     * Array of {@link Input} objects representing the state and configuration of the form inputs
     */
    inputs: Input[];
    /**
     * When true, the `validationOutputs` returned by this hook will not be updated until the
     * `handleSubmit()` method returned by the hook is called. When false, the `validationOutputs`
     * will be updated immediately and on every input change.
     * @defaultValue true
     */
    waitForSubmit?: boolean;
}

interface UseInputValidationResult {
    /**
     * Whether the `handleSubmit()` method was called
     */
    didSubmit: boolean;
    /**
     * Function to call when form data is submitted in order to validate inputs - see
     * {@link HandleSubmit}
     */
    handleSubmit: HandleSubmit;
    /**
     * True when one or more inputs have validation errors. This is evaluated immediately and on
     * every input value change.
     */
    hasErrors: boolean;
    /**
     * Method to validate the current state of `inputs` on demand
     */
    validate(): void;
    /**
     * Array of {@link ValidationOuput} objects. Each represents the result of running validation
     * rules on the current state of the `inputs` array.
     */
    validationOutputs: ValidationOutput[];
}

/**
 * @param input - {@link UseInputValidationInput}
 * @returns an object containing data and methods for form input validation - see
 * {@link UseInputValidationResult}
 */
export const useInputValidation = ({
    inputs,
    waitForSubmit = true,
}: UseInputValidationInput): UseInputValidationResult => {
    const hasErrorsRef = useRef(false);

    const validationOutputsRef = useRef<ValidationOutput[]>([]);

    const validate = useDeepCompareCallback(
        () => validateInputs(inputs),
        [inputs],
    );

    const [didSubmit, setDidSubmit] = useState(false);

    const handleSubmit = useDeepCompareCallback(
        ({ onError, onSuccess }: OnSubmitInput): void => {
            setDidSubmit(true);

            if (hasErrorsRef.current) {
                onError(validationOutputsRef?.current ?? []);
            } else {
                onSuccess(inputs);
            }
        },
        [inputs, hasErrorsRef.current, validationOutputsRef.current],
    );

    useEffect(() => {
        const validationResults = validate();

        hasErrorsRef.current = validationResults.hasValidationErrors;

        if (!waitForSubmit || didSubmit) {
            validationOutputsRef.current = validationResults.validationOutputs;
        }
    }, [
        didSubmit,
        validate,
        waitForSubmit,
        hasErrorsRef.current,
        validationOutputsRef.current,
    ]);

    return {
        didSubmit,
        handleSubmit,
        hasErrors: hasErrorsRef.current ?? false,
        validate,
        validationOutputs: validationOutputsRef.current ?? [],
    };
};
